import { apiClient } from './client'
import type { ApiResponse, DownloadResult } from './client'
import type { AnalyticsViewDto, AttributionView, RawAnalyticsViewDto } from '@/types/analytics'
import type { ValidationError } from '@/utils/errorHandling'
import { normalizeAnalyticsView } from '@/utils/analyticsNormalize'

/** Build the `?useMock=&attributionView=` query for the range-less Web analytics calls. */
function webAnalyticsQuery(mock?: boolean, attributionView?: AttributionView): string {
    const params = new URLSearchParams()
    if (mock) params.set('useMock', 'true')
    if (attributionView) params.set('attributionView', attributionView)
    const qs = params.toString()
    return qs ? `?${qs}` : ''
}

export type OnlineEventCreateDto = {
    EventType: string
    ModeType: string
    /** Deferred to sign-off (ORB-459) — creation no longer collects it. */
    Title?: string
    // IANA timezone ID (e.g., "America/New_York") - required for Upcoming and Hybrid modes, optional for OnDemand
    TimeZone?: string
    StartDateTime?: string // Required for Upcoming/Hybrid; omit for OnDemand (API uses Web.LaunchDate)
    EndDateTime?: string  // Optional for OnDemand mode, required for Upcoming and Hybrid modes
    // BigMarker ConferenceId (optional on create; required for certain flows)
    ConferenceId?: string
}

/** Payload for importing an existing BigMarker conference — API fetches event details from BigMarker. */
export type OnlineEventImportDto = {
    ConferenceId: string
}

export type WebSchedulePatchDto = {
    StartDateTime: string // ISO 8601 DateTimeOffset - always required
    EndDateTime?: string  // ISO 8601 DateTimeOffset - optional for OnDemand, required for Upcoming and Hybrid
    // IANA timezone identifier (e.g., "America/New_York") - required for Upcoming and Hybrid, optional for OnDemand
    TimeZone?: string
    ETag: string
}

export type WebLaunchDatePatchDto = {
    LaunchDate: string // ISO 8601 Date (DateOnly) - YYYY-MM-DD format
    ETag: string
}

// File Import Summary (used for enriched audience data)
export type FileImportSummaryDto = {
    Id: string
    Name: string
}

export type WebCreateDto = {
    // ORB-306: WorkspaceId/CampaignId removed — the createWeb route is authoritative
    Name: string
    // ORB-459: no Slug at creation. It is minted from the signed-off title and enforced by the
    // sign-off gate, so there is nothing to derive one from at this point.
    Type: string
    LaunchDate: string // ISO 8601 UTC DateTime
    Description?: string
    // Required, not optional: LeadGoal is a non-nullable int on the API, so an omitted value is
    // indistinguishable from an explicit 0 once it lands. Typing it optional let callers believe
    // "unset" was a state the server could hold. It cannot — 0 means no goal.
    LeadGoal: number
    OnlineEvent?: OnlineEventCreateDto | OnlineEventImportDto
    Download?: DownloadCreateDto
    Meeting?: MeetingCreateDto
    Demo?: DemoCreateDto
    // Optional linkage to multiple Campaign File Imports (Audience sources) - empty until Web becomes Active
    IncludedFileImportIds?: string[]
}

export type OnlineEventViewDto = {
    EventType: string
    ModeType: string
    Title: string
    TimeZone?: string     // IANA timezone ID (e.g., "America/New_York") - required for Upcoming/Hybrid, optional for OnDemand
    StartDateTime: string // ISO 8601 UTC - always required (live event start or on-demand availability date)
    EndDateTime?: string  // ISO 8601 UTC - optional for OnDemand, required for Upcoming/Hybrid
    Overview?: string
    Agenda?: string[]
    Speakers?: OnlineEventSpeakerViewDto[]
    ConferenceId?: string
    /** BigMarker conference-join URL (read-only; populated by sync). Distinct from the public marketing URL on the LandingPage. */
    ConferenceJoinUrl?: string
    SpeakersSyncReviewNeeded?: boolean
    LastConferenceSyncUtc?: string
    ConferenceNotFound?: boolean
    HoldCrmSyncUntilEnd?: boolean
}

export type AssetPipelineMode = 'Strict' | 'Free'

export type WebViewDto = {
    Id: string
    ETag: string
    WorkspaceId: string
    CampaignId: string
    Name: string
    Slug?: string | null // Nullable — null for legacy webs created before slug support
    Type: string
    Status: string
    /** ORB-242: per-Web override that lets users skip the LP→Email→Other walkthrough and create assets in any order. Default Strict. */
    AssetPipelineMode: AssetPipelineMode
    ApprovalStatus?: string  // "Ready for Sign-Off" | "Signed Off" | "Feedback Submitted" | "Reopened"
    ApprovedByUserId?: string | null
    ApprovedByName?: string | null
    ApprovedAt?: string | null  // ISO 8601
    LaunchDate: string // ISO 8601 UTC DateTime
    Description?: string
    AssetId?: string
    EntryRulesJson?: string
    CompletionRulesJson?: string
    LeadGoal: number
    // Count of leads contributing toward the lead goal (view-only)
    LeadCount: number
    // Server-generated, view-only identifier to link internal forms
    InternalFormBindingId: string
    // Associated audience imports (optional / may be empty)
    IncludedFileImports?: FileImportSummaryDto[]
    OnlineEvent?: OnlineEventViewDto
    Download?: DownloadViewDto
    Meeting?: MeetingViewDto
    Demo?: DemoViewDto
    /**
     * What still has to be filled in before this Web can be signed off (ORB-390). Computed server-side
     * from the same rules the sign-off gate enforces — the dashboard renders it and owns nothing but the
     * FieldName-to-tab map. Empty means ready.
     */
    OutstandingForSignOff: ValidationError[]
}

export type OnlineEventSpeakerViewDto = {
    ContactId: string
    Name: string
    Email: string
    Title: string
    CompanyName: string
    CompanyId?: string | null
    Bio?: string | null
    LinkedInProfileUrl: string
    ProfilePicUrl?: string | null
    /** Raw per-speaker custom override (null when using the LinkedIn-derived photo). ProfilePicUrl is the resolved value to display. */
    CustomHeadshotUrl?: string | null
    SpeakerType: string
}

// Download (whitepaper / asset) DTOs
// Title is optional on all three create DTOs (ORB-459): it is deferred to sign-off, so creation sends
// the sub-entity without one. The sub-entity itself is still required — the API rejects a Download web
// with a null Download.
export type DownloadCreateDto = {
    Title?: string
    Overview?: string
}

// Meeting & Demo create DTOs (extend Download shape with CalendlyUrl)
export type MeetingCreateDto = {
    Title?: string
    Overview?: string
    CalendlyUrl?: string
}

export type DemoCreateDto = {
    Title?: string
    Overview?: string
    CalendlyUrl?: string
}

export type DownloadViewDto = {
    Title: string
    Overview?: string
    Benefits?: string[]
    FileName?: string
    FileContentType?: string
    FileSizeBytes?: number
}

// Meeting & Demo view DTOs (share base fields with Download, add CalendlyUrl)
export type MeetingViewDto = {
    Title: string
    Overview?: string
    CalendlyUrl?: string
    Benefits?: string[]
}

export type DemoViewDto = {
    Title: string
    Overview?: string
    CalendlyUrl?: string
    Benefits?: string[]
}

// Patch DTO for Download benefits
export type WebDownloadBenefitsPatchDto = {
    ETag: string
    Benefits?: string[]
}

// Patch DTO for Download details
export type WebDownloadDetailsPatchDto = {
    ETag: string
    Title?: string
    Overview?: string
}

// Patch DTOs for Meeting benefits/details
export type WebMeetingBenefitsPatchDto = { 
    ETag: string
    Benefits?: string[] 
}
export type WebMeetingDetailsPatchDto = {
    ETag: string
    Title?: string;
    Overview?: string
    CalendlyUrl?: string
}

// Patch DTOs for Demo benefits/details
export type WebDemoBenefitsPatchDto = { 
    ETag: string
    Benefits?: string[] 
}
export type WebDemoDetailsPatchDto = {
    ETag: string
    Title?: string;
    Overview?: string
    CalendlyUrl?: string
}

// Patch item DTO for speakers list (array patch). Matches server WebSpeakerPatchItemDto
export type WebSpeakerPatchItemDto = {
    ContactId: string
    Name: string
    Email: string
    Title: string
    CompanyName: string
    CompanyId?: string | null
    Bio?: string | null
    /** Optional custom headshot URL overriding the LinkedIn photo. null reverts to the LinkedIn photo. */
    CustomHeadshotUrl?: string | null
    SpeakerType: string
}

// Result of uploading a custom speaker headshot. Matches server SpeakerHeadshotUploadResultDto (camelCase).
export type SpeakerHeadshotUploadResultDto = {
    url: string
    blobPath: string
    contentHash: string
    sizeBytes: number
}

// Patch DTO for Online Event details (partial). Matches server WebOnlineEventDetailsPatchDto
export type OnlineEventDetailsPatchDto = {
    // ETag is required for optimistic concurrency control
    ETag: string
    // All other properties are optional to support partial PATCH semantics
    EventType?: string
    ModeType?: string
    Title?: string
    // IANA timezone ID (e.g., "America/New_York")
    TimeZone?: string
    StartDateTime?: string
    EndDateTime?: string
    Overview?: string
    // ConferenceId (optional; immutable once set, except when ConferenceNotFound is true)
    ConferenceId?: string
    // When true, CRM/HubSpot sync is deferred until after the live event ends (live events only)
    HoldCrmSyncUntilEnd?: boolean
}

// Patch DTO for Online Event agenda
export type WebAgendaPatchDto = {
    ETag: string
    Agenda: string[]
}

// Patch DTO for Online Event speakers
export type WebSpeakersPatchDto = {
    ETag: string
    Speakers: WebSpeakerPatchItemDto[]
}

// Update DTOs aligned with server
export type OnlineEventUpdateDto = {
    EventType: string
    ModeType: string
    Title: string
    TimeZone?: string     // Required for Upcoming/Hybrid, optional for OnDemand
    StartDateTime: string // Always required
    EndDateTime?: string  // Optional for OnDemand, required for Upcoming/Hybrid
    ConferenceId?: string
    Overview?: string
    Agenda?: string[]
    Speakers?: OnlineEventSpeakerViewDto[]
}

export type WebUpdateDto = {
    Id: string
    WorkspaceId: string
    CampaignId: string
    Name: string
    /**
     * Pass the existing web.Type through unchanged. Type is immutable
     * post-create: the API rejects any change with `WebTypeImmutable`.
     */
    Type: string
    Status: string
    LaunchDate: string // ISO 8601 Date (DateOnly)
    Description?: string
    AssetId?: string
    EntryRulesJson?: string
    CompletionRulesJson?: string
    LeadGoal: number
    OnlineEvent?: OnlineEventUpdateDto
    IncludedFileImportIds?: string[]
}

// Patch DTO for top-level mutable fields (partial update endpoint expects all required values)
export type WebTopLevelPatchDto = {
    ETag: string
    Name: string
    Slug?: string | null // undefined = no change; null = clear; string = set/update
    Meeting?: MeetingViewDto
    Demo?: DemoViewDto
    Status: string
    LaunchDate: string // ISO 8601 Date (DateOnly)
    Description?: string | null
    LeadGoal: number
    IncludedFileImportIds?: string[]
}

/** ORB-242: patch Web.AssetPipelineMode (Strict | Free). ETag required for optimistic concurrency. */
export type WebAssetPipelineModePatchDto = {
    ETag: string
    Mode: AssetPipelineMode
}

export type WebSignOffDto = {
    ETag: string
    Confirmed: boolean
    /** Public URL segment, chosen in the sign-off panel (ORB-482). Omitted leaves whatever is stored. */
    Slug?: string
}

export type WebReopenDto = {
    ETag: string
}

export const websApi = {
    // Check whether a proposed Web slug is available within the given workspace.
    checkSlug: (workspaceId: string, slug: string): Promise<ApiResponse<{ slug: string; available: boolean }>> =>
        apiClient.get(`/api/Workspaces/${workspaceId}/Webs/CheckSlug?slug=${encodeURIComponent(slug)}`),

    createWeb: (clientCompanyId: string, workspaceId: string, campaignId: string, data: WebCreateDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs`, data),

    updateWeb: (webId: string, data: WebUpdateDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.request(`/api/Webs/UpdateWeb/${webId}`, { method: 'PATCH', body: data }),

    // Patch web schedule (for calendar drag-and-drop of online events)
    patchSchedule: (workspaceId: string, id: string, dto: WebSchedulePatchDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.request(`/api/Workspaces/${workspaceId}/Webs/${id}/Schedule`, { method: 'PATCH', body: dto }),

    // Patch web launch date (for calendar drag-and-drop of launch date events)
    patchLaunchDate: (workspaceId: string, id: string, dto: WebLaunchDatePatchDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.request(`/api/Workspaces/${workspaceId}/Webs/${id}/LaunchDate`, { method: 'PATCH', body: dto }),

    getById: (workspaceId: string, webId: string): Promise<ApiResponse<WebViewDto>> =>
        apiClient.get(`/api/Webs/${workspaceId}/${webId}`),

    // Patch only the agenda for a web (new endpoint). Returns 200 with WebViewDto on success
    updateAgenda: (workspaceId: string, webId: string, etag: string, agenda: string[]): Promise<ApiResponse<WebViewDto>> =>
        apiClient.request(`/api/Webs/${workspaceId}/${webId}/agenda`, { method: 'PATCH', body: { ETag: etag, Agenda: agenda } }),

    // Patch Online Event details (server route: Webs/{workspaceId}/{webId}/details). Returns 200 with WebViewDto on success
    updateOnlineEventDetails: (workspaceId: string, webId: string, dto: OnlineEventDetailsPatchDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.request(`/api/Webs/${workspaceId}/${webId}/details`, { method: 'PATCH', body: dto }),

    // Delete a web (hard delete). Server route: Webs/{workspaceId}/{campaignId}/{webId} returns 204
    deleteWeb: (workspaceId: string, campaignId: string, webId: string, deleteBigMarkerConference?: boolean): Promise<ApiResponse<void>> =>
        apiClient.request(`/api/Webs/${workspaceId}/${campaignId}/${webId}${deleteBigMarkerConference ? '?deleteBigMarkerConference=true' : ''}`, { method: 'DELETE' }),

    // Sign off a web (triggers BigMarker sync). Returns updated WebViewDto
    signOff: (workspaceId: string, campaignId: string, webId: string, dto: WebSignOffDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.post(`/api/Webs/${workspaceId}/${campaignId}/${webId}/SignOff`, dto),

    // Reopen a signed-off web. Returns updated WebViewDto
    reopen: (workspaceId: string, campaignId: string, webId: string, dto: WebReopenDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.post(`/api/Webs/${workspaceId}/${campaignId}/${webId}/Reopen`, dto),

    // Trigger a BigMarker conference sync. Returns updated WebViewDto
    syncConference: (workspaceId: string, webId: string): Promise<ApiResponse<WebViewDto>> =>
        apiClient.post(`/api/Webs/${workspaceId}/${webId}/Sync`, {}),

    // Patch top-level fields (Name, Status, LeadGoal, Description) route: Webs/{workspaceId}/{webId} returns 200 with WebViewDto
    patchTopLevel: (workspaceId: string, webId: string, dto: WebTopLevelPatchDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.request(`/api/Webs/${workspaceId}/${webId}`, { method: 'PATCH', body: dto }),

    // ORB-242: flip per-Web AssetPipelineMode (Strict | Free). Returns updated WebViewDto.
    patchAssetPipelineMode: (workspaceId: string, webId: string, dto: WebAssetPipelineModePatchDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.request(`/api/Workspaces/${workspaceId}/Webs/${webId}/AssetPipelineMode`, { method: 'PATCH', body: dto }),

    // Patch speakers list route: Webs/{workspaceId}/{webId}/speakers returns 200 with WebViewDto
    patchSpeakers: (workspaceId: string, webId: string, etag: string, speakers: WebSpeakerPatchItemDto[]): Promise<ApiResponse<WebViewDto>> =>
        apiClient.request(`/api/Webs/${workspaceId}/${webId}/speakers`, { method: 'PATCH', body: { ETag: etag, Speakers: speakers } }),

    // Upload a custom speaker headshot (multipart, single field `file`). Returns a public URL to store as CustomHeadshotUrl on the speaker patch.
    uploadSpeakerHeadshot: (workspaceId: string, webId: string, file: File): Promise<ApiResponse<SpeakerHeadshotUploadResultDto>> => {
        const formData = new FormData()
        formData.append('file', file)
        return apiClient.post(`/api/Webs/${workspaceId}/${webId}/speakers/headshots`, formData)
    },

    // Patch download benefits route: Webs/{workspaceId}/{webId}/download/benefits returns 200 with WebViewDto
    updateDownloadBenefits: (workspaceId: string, webId: string, dto: WebDownloadBenefitsPatchDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.request(`/api/Webs/${workspaceId}/${webId}/download/benefits`, { method: 'PATCH', body: dto }),

    // Patch download details route: Webs/{workspaceId}/{webId}/download/details returns 200 with WebViewDto
    updateDownloadDetails: (workspaceId: string, webId: string, dto: WebDownloadDetailsPatchDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.request(`/api/Webs/${workspaceId}/${webId}/download/details`, { method: 'PATCH', body: dto }),

    // Patch meeting benefits route: Webs/{workspaceId}/{webId}/meeting/benefits returns 200 with WebViewDto
    updateMeetingBenefits: (workspaceId: string, webId: string, dto: WebMeetingBenefitsPatchDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.request(`/api/Webs/${workspaceId}/${webId}/meeting/benefits`, { method: 'PATCH', body: dto }),

    // Patch meeting details route: Webs/{workspaceId}/${webId}/meeting/details returns 200 with WebViewDto
    updateMeetingDetails: (workspaceId: string, webId: string, dto: WebMeetingDetailsPatchDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.request(`/api/Webs/${workspaceId}/${webId}/meeting/details`, { method: 'PATCH', body: dto }),

    // Patch demo benefits route: Webs/{workspaceId}/${webId}/demo/benefits returns 200 with WebViewDto
    updateDemoBenefits: (workspaceId: string, webId: string, dto: WebDemoBenefitsPatchDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.request(`/api/Webs/${workspaceId}/${webId}/demo/benefits`, { method: 'PATCH', body: dto }),

    // Patch demo details route: Webs/{workspaceId}/${webId}/demo/details returns 200 with WebViewDto
    updateDemoDetails: (workspaceId: string, webId: string, dto: WebDemoDetailsPatchDto): Promise<ApiResponse<WebViewDto>> =>
        apiClient.request(`/api/Webs/${workspaceId}/${webId}/demo/details`, { method: 'PATCH', body: dto }),

    // Upload a downloadable file for a Download web type. Returns updated WebViewDto with FileName/FileContentType/FileSizeBytes
    uploadDownloadFile: (workspaceId: string, webId: string, file: File): Promise<ApiResponse<WebViewDto>> => {
        const formData = new FormData()
        formData.append('file', file)
        return apiClient.post(`/api/Workspaces/${workspaceId}/Webs/${webId}/Download/File`, formData)
    },

    // ORB-328 / ORB-373: web-level landing page analytics. Range-less — the API returns
    // Current counts only (PreviousWindow / Delta are null at this level). Resolves the
    // Web's landing page server-side; workspaceId is the auth boundary. `attributionView`
    // flips credited numbers between conversion-session (default) and first-seen.
    getAnalytics: async (workspaceId: string, webId: string, mock?: boolean, attributionView?: AttributionView): Promise<ApiResponse<AnalyticsViewDto>> => {
        const base = `/api/Workspaces/${encodeURIComponent(workspaceId)}/Webs/${encodeURIComponent(webId)}/Analytics`
        const resp = await apiClient.get<RawAnalyticsViewDto>(`${base}${webAnalyticsQuery(mock, attributionView)}`)
        return { ...resp, data: resp.data ? normalizeAnalyticsView(resp.data) : undefined }
    },

    // ORB-373: download a CSV export of this Web's visitor/session data. Gated server-side
    // by the export capability (403 → handled globally). Honours the active attribution view.
    exportAnalytics: (workspaceId: string, webId: string, mock?: boolean, attributionView?: AttributionView): Promise<DownloadResult> => {
        const base = `/api/Workspaces/${encodeURIComponent(workspaceId)}/Webs/${encodeURIComponent(webId)}/Analytics/Export`
        return apiClient.download(`${base}${webAnalyticsQuery(mock, attributionView)}`)
    },
}
